import React, { useState, useEffect, memo } from 'react';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { MenuVertical, PenIcon, TrashIcon } from '~/assets/icons';
import styles from './ClientList.module.scss';
import classNames from 'classnames/bind';
import logo from '~/assets/images/logo.svg';
import { BaseImage, BaseMenu } from '~/components/common';
import { LinearProgress, MenuItem } from '@mui/material';
import TimeLeftLabel from '~/components/jobs/time-left-label/TimeLeftLabel';
import StatusLabel from '~/components/jobs/status-label/StatusLabel';
import { useTranslation } from 'react-i18next';
import BaseTable from '~/components/common/base-table/BaseTable';
import { IProjects } from '~/utils/interface/project';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleGetProjects } from '~/thunks/project/projectThunk';
import { ROWS_PER_PAGE, DEFAULT_PAGE } from '~/utils/constants/common';
import { projectActions } from '~/thunks/project/projectSlice';
import { SortDirection } from '~/utils/types/common';
import dayjs, { ManipulateType } from 'dayjs';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Icon from '@mui/material/Icon';

const cx = classNames.bind(styles);

type ClientListProps = {
  setSelectedIds: (selectedIds: Array<string>) => void;
};

function ClientList(props: ClientListProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { setSelectedIds } = props;

  const { projects, statusMode, searchInput, totalPages, sortBy, project_dummy } = useAppSelector((state) => state.project);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(
      handleGetProjects({
        page: DEFAULT_PAGE,
        limit: ROWS_PER_PAGE,
        status: statusMode !== 'all' ? statusMode : undefined,
        search: searchInput,
        sort: sortBy,
      })
    );
  }, [dispatch, statusMode, searchInput, sortBy]);

  const { t } = useTranslation('admin/common');

  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const colors = (status: string) => {
    const colors = {
      completed: '#E5FDE1',
      inprogress: '#EBEDF4',
      on_hold: '#FDE1E1'
    };

    return colors.completed;
  };

  useEffect(() => {
    setSelectedIds(selected);
  }, [selected, setSelectedIds]);

  const handleChangePage = (newPage: number) => {
    dispatch(
      handleGetProjects({
        page: newPage,
        limit: ROWS_PER_PAGE,
        status: statusMode !== 'all' ? statusMode : undefined,
        search: searchInput,
        sort: sortBy,
      })
    );
  };

  const handleRequestSort = (orderBy: string, order: SortDirection) => {
    dispatch(projectActions.changeSortBy({ field: orderBy, type: order }));
  };

  const headCells = [
    {
      id: 'client_name',
      label: 'Client Name',
      sortable: true,
    },
    {
      id: 'country',
      label: 'Country',
      sortable: true,
    },
    {
      id: 'phone_number',
      label: 'Phone Number',
      sortable: true,
    },
    {
      id: 'email',
      label: 'Email Address',
      sortable: true,
    },
    {
      id: 'name',
      label: 'Project Name',
      sortable: true,
    },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
    },
    {
      id: 'date',
      label: 'Date',
      sortable: true,
    },
    {
      id: 'action',
      label: 'Action',
      sortable: false,
    },
  ];

  return (
    <Box className={cx('job-list-container')}>
      <BaseTable<IProjects>
        data={project_dummy}
        searchInput={searchInput}
        filterBy={[]}
        sortBy={[]}
        setSelectedIds={setSelected}
        headCells={headCells}
        onChangePage={handleChangePage}
        onRequestSort={handleRequestSort}
        totalPages={totalPages}
        renderCell={(project, index: number) => {
          const progressPercent = Math.round((project.trackTime / project.estimate) * 100);
          return (
            <>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('project-name-container')}>
                  <Avatar className={cx('image', 'projectName')} sx={{ bgcolor: '#fe06d3'}}>CN</Avatar>
                  <Box className={cx('projectName')}>{project.client_name}</Box>
                </Box>
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('project-name-container')}>
                  <BaseImage src='/flags/england.png' className={cx('image', 'projectName')} />
                  <Box className={cx('projectName')}>{project.country}</Box>
                </Box>
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('project-name-container')}>
                  <Box className={cx('projectName')}>{project.phone_number}</Box>
                </Box>
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('project-name-container')}>
                  <Box className={cx('projectName')}>{project.email}</Box>
                </Box>
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('project-name-container')}>
                  <Box className={cx('projectName')}>{project.project_name}</Box>
                </Box>
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('project-name-container')}>
                  <Box className={cx('projectName')}>
                    <Chip className={colors(project.status)} label={project.status} size="small" />
                  </Box>
                </Box>
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('project-name-container')}>
                  <Box className={cx('projectName')}>{dayjs(project.date).format('D MMMM, YYYY')}</Box>
                </Box>
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('project-name-container')}>
                  <Box className={cx('projectName')}>...</Box>
                </Box>
                <BaseMenu className='flex w-full' anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
                  <MenuItem>
                    <Box className='flex flex-row items-center p-[6px] w-[169px]'>
                      <PenIcon className='w-[13px] h-[13px]' />
                      <Box className='ml-[6px] text-[14px]/[16.8px] font-normal text-[#5A6385]'>{t('edit')}</Box>
                    </Box>
                  </MenuItem>
                  <MenuItem className='mt-[10px] bg-[#FFF0F0]' id='danger'>
                    <Box className='flex flex-row items-center p-[6px] w-[169px] '>
                      <TrashIcon className='w-[13px] h-[16px]' />
                      <Box className='ml-[6px] text-[14px]/[16.8px] font-normal text-[#FC0000]'>{t('delete')}</Box>
                    </Box>
                  </MenuItem>
                </BaseMenu>
              </TableCell>
            </>
          );
        }}
      />


    </Box>
  );
}

export default memo(ClientList);
